// The workspace's list title: the current view as a menu of the others (My tickets, Review, Follow-ups).

import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type WorkspaceView = 'tickets' | 'automation' | 'followups';

export interface WorkspaceViewCounts {
  tickets?: number;
  review?: number;
  /** Follow-ups a person must act on: drafts to review and hand-offs. */
  followupsNeedYou?: number;
  /** Follow-ups still waiting on the customer. */
  followupsLive?: number;
}

const VIEWS: { value: WorkspaceView; label: string; description: string }[] = [
  { value: 'tickets', label: 'My tickets', description: 'Your ticket queue' },
  { value: 'automation', label: 'Review', description: 'Automated replies to check' },
  { value: 'followups', label: 'Follow-ups', description: 'Waiting on customers' },
];

const needsYou = (view: WorkspaceView, counts: WorkspaceViewCounts) =>
  view === 'automation' ? (counts.review ?? 0) > 0 : view === 'followups' ? (counts.followupsNeedYou ?? 0) > 0 : false;

/** The number shown beside a view, and whether it is asking for the user. */
function viewCount(view: WorkspaceView, counts: WorkspaceViewCounts): { text: string; urgent: boolean } | null {
  if (view === 'tickets') return counts.tickets === undefined ? null : { text: String(counts.tickets), urgent: false };
  if (view === 'automation') return counts.review === undefined ? null : { text: String(counts.review), urgent: false };
  if (counts.followupsNeedYou) return { text: `${counts.followupsNeedYou} need you`, urgent: true };
  return counts.followupsLive === undefined ? null : { text: String(counts.followupsLive), urgent: false };
}

export function ViewPicker({
  view,
  counts,
  onChange,
}: {
  view: WorkspaceView;
  counts: WorkspaceViewCounts;
  onChange: (view: WorkspaceView) => void;
}) {
  const current = VIEWS.find((option) => option.value === view)!;
  const currentCount = viewCount(view, counts);
  const attention = VIEWS.some((option) => option.value !== view && needsYou(option.value, counts));

  return (
    <>
      {/* The page's name for screen readers; the menu button below is how it reads on screen. */}
      <h1 className="sr-only">{current.label}</h1>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="-ml-2 h-8 shrink-0 gap-1.5 px-2 text-[16px] font-medium leading-[1.1] whitespace-nowrap text-text-body"
          >
            {current.label}
            {/* "3 need you", not a bare 3: the tabs below show other counts (due, waiting). */}
            {currentCount && (
              <span className={`text-[13px] font-normal tabular-nums ${currentCount.urgent ? 'text-violet-600' : 'text-gray-700'}`}>
                {currentCount.text}
              </span>
            )}
            <ChevronDown className="size-3.5 text-gray-700" aria-hidden />
            {attention && (
              <>
                <span className="size-1.5 rounded-full bg-violet-500" aria-hidden data-testid="view-attention" />
                <span className="sr-only">, another view needs you</span>
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 rounded-xl bg-white p-1">
          <DropdownMenuRadioGroup value={view} onValueChange={(value) => onChange(value as WorkspaceView)}>
            {VIEWS.map((option) => {
              const count = viewCount(option.value, counts);
              const selected = option.value === view;
              return (
                <DropdownMenuRadioItem
                  key={option.value}
                  value={option.value}
                  // The primitive's dot indicator is hidden: this menu marks the current view with a check.
                  className="items-start gap-2 rounded-lg py-2 pr-2 pl-2 focus:text-text-body [&>span:first-child]:hidden"
                >
                  <Check className={cn('mt-0.5 size-3.5 text-text-body', !selected && 'invisible')} aria-hidden />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-[13px] font-medium leading-[18px] text-text-body">{option.label}</span>
                    <span className="text-[12px] leading-4 text-gray-700">{option.description}</span>
                  </span>
                  {count && (
                    <span
                      className={cn(
                        'text-[12px] leading-[18px] tabular-nums whitespace-nowrap',
                        count.urgent ? 'font-medium text-violet-600' : 'text-gray-700',
                      )}
                    >
                      {count.text}
                    </span>
                  )}
                </DropdownMenuRadioItem>
              );
            })}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

/** An icon button beside the picker, named for screen readers and on hover. */
export function ListIconButton({ label, children, ...props }: React.ComponentProps<typeof Button> & { label: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="text-gray-900" aria-label={label} {...props}>
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
